import React from 'react';

import { Icon } from '../../../common/components/Icon';
import { OrbitIcons } from '../../../common/constants.';

import type { WizardStepKey, WizardStepMeta } from './types';

interface WizardSidebarProps {
  /** Steps in the current flow (filtered by FreeSWITCH type / Cosmos IVR) */
  steps: WizardStepMeta[];
  currentStep: WizardStepKey;
  completedSteps: Set<WizardStepKey>;
  onStepClick: (step: WizardStepKey) => void;
}

type StepStatus = 'completed' | 'active' | 'pending';

const getStepStatus = (
  step: WizardStepKey,
  currentStep: WizardStepKey,
  completedSteps: Set<WizardStepKey>
): StepStatus => {
  if (completedSteps.has(step)) return 'completed';
  if (step === currentStep) return 'active';
  return 'pending';
};

const StepItem: React.FC<{
  step: WizardStepMeta;
  status: StepStatus;
  index: number;
  isLast: boolean;
  onClick: () => void;
}> = ({ step, status, index, isLast, onClick }) => {
  const isClickable = status === 'completed';

  return (
    <div className="relative flex items-start">
      {/* Vertical line connecting steps */}
      {!isLast && (
        <div
          className={`absolute left-4 top-8 h-full w-0.5 ${
            status === 'completed' ? 'bg-blue-500' : 'bg-gray-200'
          }`}
        />
      )}

      <button
        type="button"
        onClick={onClick}
        disabled={!isClickable}
        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors ${
          status === 'active'
            ? 'bg-blue-50 text-blue-700'
            : status === 'completed'
            ? 'cursor-pointer text-gray-700 hover:bg-gray-50'
            : 'cursor-default text-gray-400'
        }`}
      >
        {/* Step icon */}
        <div
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
            status === 'completed'
              ? 'bg-blue-500 text-white'
              : status === 'active'
              ? 'border-2 border-blue-500 bg-white text-blue-500'
              : 'border-2 border-gray-300 bg-white text-gray-400'
          }`}
        >
          {status === 'completed' ? (
            <Icon name={OrbitIcons.Check} size={16} />
          ) : (
            <span className="text-sm font-medium">{index + 1}</span>
          )}
        </div>

        {/* Step label */}
        <span
          className={`text-sm font-medium ${
            status === 'active' ? 'text-blue-700' : status === 'completed' ? 'text-gray-700' : ''
          }`}
        >
          {step.label}
        </span>
      </button>
    </div>
  );
};

const WizardSidebar: React.FC<WizardSidebarProps> = ({
  steps,
  currentStep,
  completedSteps,
  onStepClick,
}) => {
  const totalSteps = steps.length;
  const completedCount = completedSteps.size;
  const progressPercent = Math.round((completedCount / totalSteps) * 100);

  return (
    <aside className="w-72 bg-gray-50 flex flex-col h-full overflow-hidden border-r border-gray-200">
      {/* Header */}
      <div className="shrink-0 p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Crear Cuenta</h2>
        <p className="mt-1 text-sm text-gray-500">Configura una nueva cuenta de telefonía</p>
      </div>

      {/* Steps list */}
      <nav className="flex-1 min-h-0 p-4 overflow-y-auto">
        <div className="space-y-1">
          {steps.map((step, index) => (
            <StepItem
              key={step.key}
              step={step}
              status={getStepStatus(step.key, currentStep, completedSteps)}
              index={index}
              isLast={index === steps.length - 1}
              onClick={() => onStepClick(step.key)}
            />
          ))}
        </div>
      </nav>

      {/* Progress bar */}
      <div className="shrink-0 p-4 border-t border-gray-200">
        <div className="flex items-center justify-between mb-2 text-sm">
          <span className="text-gray-600">Progreso</span>
          <span className="font-medium text-gray-900">{progressPercent}%</span>
        </div>
        <div className="h-2 overflow-hidden bg-gray-200 rounded-full">
          <div
            className="h-full transition-all duration-300 bg-blue-500 rounded-full"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-gray-500">
          {completedCount} de {totalSteps} pasos completados
        </p>
      </div>
    </aside>
  );
};

export default WizardSidebar;
